import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import AddressAutocomplete from '../components/AddressAutocomplete';
import FloatingTacoWidget from '../components/FloatingTacoWidget';
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Loader2,
  Search,
  Package,
  DollarSign,
  ClipboardList,
  Plus,
  X,
  CheckSquare,
  Users,
  User,
  Wrench,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const NewBookingPage = () => {
  const navigate = useNavigate();
  const { api, user, isDirector, isManager } = useAuth();
  const canViewFinancials = user?.permissions?.view_financials || isDirector;
  
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showClientSearch, setShowClientSearch] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    service_address: '',
    scheduled_date: new Date(),
    time_window_start: '09:00',
    time_window_end: '12:00',
    job_details: '',
    access_notes: '',
    estimated_duration_minutes: 120,
    price: '',
    template_id: '',
    deposit_required: false,
    is_recurring: false,
    recurrence_pattern: '',
    // Staff assignment
    staff_count: 1,  // 1 or 2 staff
    assigned_staff_ids: [],  // Selected staff members
    // Service Package fields
    package_id: '',
    package_variant_id: '',
    package_matrix_row_id: '',
    job_value: '',
    // Custom price override (editable)
    custom_price_override: '',
    // Extras/Add-ons
    extras: [],
    // Price notes for special conditions
    price_notes: '',
    // Custom checklist items (for ad-hoc tasks)
    custom_checklist_items: [],
  });
  
  // Custom checklist input state
  const [newChecklistItem, setNewChecklistItem] = useState('');
  // Extras input state
  const [newExtraName, setNewExtraName] = useState('');
  const [newExtraPrice, setNewExtraPrice] = useState('');

  useEffect(() => {
    fetchClients();
    fetchTemplates();
    fetchServicePackages();
    fetchStaff();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api().get('/clients');
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api().get('/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchServicePackages = async () => {
    try {
      const response = await api().get('/bookings/packages');
      setServicePackages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch service packages:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api().get('/users/staff/available');
      setStaffList(response.data || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.service_address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectClient = (client) => {
    setFormData({
      ...formData,
      client_id: client.id,
      client_name: client.name,
      service_address: client.service_address,
      access_notes: client.access_instructions || '',
    });
    setShowClientSearch(false);
    setSearchTerm('');
  };

  const selectTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        template_id: templateId,
        estimated_duration_minutes: template.default_duration_minutes || formData.estimated_duration_minutes,
        price: template.default_price?.toString() || formData.price,
        job_details: template.description || formData.job_details,
      });
    }
  };

  const selectPackage = (packageId) => {
    // Handle "custom" package - no preset checklist, manual price
    if (packageId === 'custom') {
      setFormData({
        ...formData,
        package_id: 'custom',
        package_variant_id: '',
        package_matrix_row_id: '',
        job_value: '',  // Manual price required
        custom_checklist_items: [],  // Start with empty checklist
      });
      return;
    }
    
    const pkg = servicePackages.find(p => p.id === packageId);
    if (pkg) {
      let price = 0;
      if (pkg.pricing_model === 'flat') {
        price = pkg.flat_price_ex_gst;
      } else if (pkg.pricing_model === 'variant' && pkg.variants?.length > 0) {
        price = pkg.variants[0].price_ex_gst;
      }
      setFormData({
        ...formData,
        package_id: packageId,
        package_variant_id: pkg.pricing_model === 'variant' && pkg.variants?.length > 0 ? pkg.variants[0].id : '',
        package_matrix_row_id: '',
        job_value: price.toString(),
        job_details: pkg.description || formData.job_details,
      });
    } else {
      setFormData({
        ...formData,
        package_id: '',
        package_variant_id: '',
        package_matrix_row_id: '',
        job_value: '',
      });
    }
  };

  const selectVariant = (variantId) => {
    const pkg = servicePackages.find(p => p.id === formData.package_id);
    const variant = pkg?.variants?.find(v => v.id === variantId);
    if (variant) {
      setFormData({
        ...formData,
        package_variant_id: variantId,
        job_value: variant.price_ex_gst.toString(),
      });
    }
  };

  const selectMatrixRow = (rowId) => {
    const pkg = servicePackages.find(p => p.id === formData.package_id);
    const row = pkg?.matrix_rows?.find(r => r.id === rowId);
    if (row) {
      setFormData({
        ...formData,
        package_matrix_row_id: rowId,
        job_value: row.price_ex_gst.toString(),
      });
    }
  };

  const selectedPackage = servicePackages.find(p => p.id === formData.package_id);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price);
  };
  
  // Add custom checklist item
  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    const newItem = {
      id: `custom-${Date.now()}`,
      task: newChecklistItem.trim(),
      group: 'Additional Tasks',
      required: true,
      photo_required: false,
      completed: false,
      is_custom: true,
    };
    
    setFormData({
      ...formData,
      custom_checklist_items: [...formData.custom_checklist_items, newItem],
    });
    setNewChecklistItem('');
  };
  
  // Remove custom checklist item
  const removeChecklistItem = (itemId) => {
    setFormData({
      ...formData,
      custom_checklist_items: formData.custom_checklist_items.filter(item => item.id !== itemId),
    });
  };

  // Add extra/add-on
  const addExtra = () => {
    if (!newExtraName.trim() || !newExtraPrice) return;
    
    const price = parseFloat(newExtraPrice);
    if (isNaN(price)) {
      toast.error('Please enter a valid price');
      return;
    }
    
    const newExtra = {
      id: `extra-${Date.now()}`,
      name: newExtraName.trim(),
      price: price,
      quantity: 1,
    };
    
    setFormData({
      ...formData,
      extras: [...formData.extras, newExtra],
    });
    setNewExtraName('');
    setNewExtraPrice('');
  };
  
  // Remove extra
  const removeExtra = (extraId) => {
    setFormData({
      ...formData,
      extras: formData.extras.filter(e => e.id !== extraId),
    });
  };
  
  // Calculate total extras price
  const extrasTotal = formData.extras.reduce((sum, e) => sum + (e.price * (e.quantity || 1)), 0);
  
  // Calculate prices
  // - basePrice: The standard package price
  // - packagePrice: The effective package price (base or overridden for larger houses)
  // - finalPrice: Package price + Extras = Total
  const basePrice = parseFloat(formData.job_value) || 0;
  const packagePrice = formData.custom_price_override 
    ? parseFloat(formData.custom_price_override) 
    : basePrice;
  const finalPrice = packagePrice + extrasTotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    
    if (!formData.service_address) {
      toast.error('Please enter a service address');
      return;
    }
    
    // Validate custom package has a price
    if (formData.package_id === 'custom' && !formData.custom_price_override && !formData.job_value) {
      toast.error('Please enter a price for the custom package');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        client_id: formData.client_id,
        service_address: formData.service_address,
        scheduled_date: formData.scheduled_date.toISOString(),
        time_window_start: formData.time_window_start,
        time_window_end: formData.time_window_end,
        job_details: formData.job_details || null,
        access_notes: formData.access_notes || null,
        estimated_duration_minutes: parseInt(formData.estimated_duration_minutes) || null,
        price: formData.price ? parseFloat(formData.price) : null,
        template_id: formData.template_id || null,
        deposit_required: formData.deposit_required,
        is_recurring: formData.is_recurring,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
        // Staff assignment
        staff_count: formData.staff_count,
        assigned_staff_id: formData.assigned_staff_ids[0] || null,  // Primary staff for backward compatibility
        assigned_staff_ids: formData.assigned_staff_ids.length > 0 ? formData.assigned_staff_ids : null,
        // Service Package fields - handle 'custom' as null package_id
        package_id: formData.package_id === 'custom' ? null : (formData.package_id || null),
        package_variant_id: formData.package_variant_id || null,
        package_matrix_row_id: formData.package_matrix_row_id || null,
        job_value: formData.job_value ? parseFloat(formData.job_value) : (formData.custom_price_override ? parseFloat(formData.custom_price_override) : null),
        // Price override and extras
        custom_price_override: formData.custom_price_override ? parseFloat(formData.custom_price_override) : null,
        extras: formData.extras.length > 0 ? formData.extras : null,
        extras_total: extrasTotal > 0 ? extrasTotal : null,
        price_notes: formData.price_notes || null,
        // Custom checklist items (ad-hoc tasks added by admin)
        custom_checklist_items: formData.custom_checklist_items.length > 0 ? formData.custom_checklist_items : null,
        // For custom package, set a name snapshot
        package_name_snapshot: formData.package_id === 'custom' ? 'Custom' : null,
      };
      
      const response = await api().post('/bookings', payload);
      toast.success('Booking created successfully!');
      navigate(`/bookings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
    '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00'
  ];

  return (
    <div className="space-y-6 pb-20 md:pb-0 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/bookings')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">New Booking</h1>
          <p className="text-zinc-500 text-sm">Create a quick booking</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Client</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Label>Select Client</Label>
              {formData.client_id ? (
                <div className="mt-2 p-3 bg-zinc-50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-zinc-900">{formData.client_name}</p>
                    <p className="text-sm text-zinc-500">{formData.service_address}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFormData({ ...formData, client_id: '', client_name: '', service_address: '' });
                      setShowClientSearch(true);
                    }}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <div className="mt-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                    <Input
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowClientSearch(true);
                      }}
                      onFocus={() => setShowClientSearch(true)}
                      className="pl-10"
                      data-testid="client-search-input"
                    />
                  </div>
                  {showClientSearch && searchTerm && (
                    <div className="absolute z-10 mt-1 w-full bg-white rounded-lg border border-zinc-200 shadow-lg max-h-60 overflow-auto">
                      {filteredClients.length === 0 ? (
                        <div className="p-4 text-center text-zinc-500">
                          No clients found
                        </div>
                      ) : (
                        filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="p-3 hover:bg-zinc-50 cursor-pointer border-b border-zinc-100 last:border-0"
                            onClick={() => selectClient(client)}
                            data-testid={`client-option-${client.id}`}
                          >
                            <p className="font-medium text-zinc-900">{client.name}</p>
                            <p className="text-sm text-zinc-500">{client.service_address}</p>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="service_address">Service Address</Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={formData.service_address}
                  onChange={(address) => setFormData({ ...formData, service_address: address })}
                  placeholder="Search or enter address..."
                  data-testid="service-address-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Package Selection */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-zinc-500" />
              Service Package
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select Package</Label>
              <Select
                value={formData.package_id || "none"}
                onValueChange={(value) => selectPackage(value === "none" ? "" : value)}
              >
                <SelectTrigger className="mt-2" data-testid="package-select">
                  <SelectValue placeholder="Choose a package" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Package</SelectItem>
                  {servicePackages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - {pkg.pricing_model === 'flat' ? formatPrice(pkg.flat_price_ex_gst) : 'Variable'}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">
                    <span className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Custom Package
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Package Notice */}
            {formData.package_id === 'custom' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">Custom Package</p>
                <p className="text-xs text-blue-600 mt-1">
                  Build your own checklist below and set a manual price. Perfect for jobs that don't fit standard packages.
                </p>
              </div>
            )}

              {/* Variant Selection for VARIANT pricing */}
              {selectedPackage?.pricing_model === 'variant' && selectedPackage.variants?.length > 0 && (
                <div>
                  <Label>Crew Configuration</Label>
                  <Select
                    value={formData.package_variant_id || undefined}
                    onValueChange={selectVariant}
                  >
                    <SelectTrigger className="mt-2" data-testid="variant-select">
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedPackage.variants.map((variant) => (
                        <SelectItem key={variant.id} value={variant.id}>
                          {variant.name} - {formatPrice(variant.price_ex_gst)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Matrix Selection for MATRIX pricing */}
              {selectedPackage?.pricing_model === 'matrix' && selectedPackage.matrix_rows?.length > 0 && (
                <div>
                  <Label>Property Configuration</Label>
                  <Select
                    value={formData.package_matrix_row_id || undefined}
                    onValueChange={selectMatrixRow}
                  >
                    <SelectTrigger className="mt-2" data-testid="matrix-select">
                      <SelectValue placeholder="Select property size" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedPackage.matrix_rows.map((row) => (
                        <SelectItem key={row.id} value={row.id}>
                          {row.story_type === 'single' ? 'Single Story' : 'Double Story'} - {row.bed_bath_tier} - {formatPrice(row.price_ex_gst)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Package Summary */}
              {selectedPackage && (
                <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Package:</span>
                    <span className="font-medium text-zinc-900">{selectedPackage.name}</span>
                  </div>
                  {canViewFinancials && formData.job_value && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-600 flex items-center gap-1">
                        <DollarSign className="h-3 w-3" /> Base Price (ex GST):
                      </span>
                      <span className="font-semibold text-zinc-700">{formatPrice(parseFloat(formData.job_value))}</span>
                    </div>
                  )}
                  {selectedPackage.checklist_items?.length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-600 flex items-center gap-1">
                        <ClipboardList className="h-3 w-3" /> Checklist:
                      </span>
                      <span className="text-zinc-700">{selectedPackage.checklist_items.length} items</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

        {/* Staff Assignment */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-zinc-500" />
              Staff Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Staff Count */}
            <div>
              <Label>Number of Staff Required</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={formData.staff_count === 1 ? "default" : "outline"}
                  className={formData.staff_count === 1 ? "flex-1" : "flex-1"}
                  onClick={() => setFormData({ ...formData, staff_count: 1, assigned_staff_ids: formData.assigned_staff_ids.slice(0, 1) })}
                  data-testid="staff-count-1"
                >
                  <User className="h-4 w-4 mr-2" />
                  1 Staff
                </Button>
                <Button
                  type="button"
                  variant={formData.staff_count === 2 ? "default" : "outline"}
                  className={formData.staff_count === 2 ? "flex-1" : "flex-1"}
                  onClick={() => setFormData({ ...formData, staff_count: 2 })}
                  data-testid="staff-count-2"
                >
                  <Users className="h-4 w-4 mr-2" />
                  2 Staff
                </Button>
              </div>
            </div>

            {/* Staff Selection */}
            <div className="space-y-3">
              <Label>Assign Staff {formData.staff_count > 1 && "(Optional - can assign later)"}</Label>
              
              {/* Primary Staff */}
              <Select
                value={formData.assigned_staff_ids[0] || "none"}
                onValueChange={(value) => {
                  const newIds = value === "none" ? [] : [value, ...(formData.assigned_staff_ids.slice(1) || [])];
                  setFormData({ ...formData, assigned_staff_ids: newIds.filter(Boolean) });
                }}
              >
                <SelectTrigger data-testid="staff-select-1">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Assign Later</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id} disabled={formData.assigned_staff_ids.includes(staff.id)}>
                      {staff.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Secondary Staff (if 2 staff selected) */}
              {formData.staff_count === 2 && (
                <Select
                  value={formData.assigned_staff_ids[1] || "none"}
                  onValueChange={(value) => {
                    const primary = formData.assigned_staff_ids[0];
                    const newIds = value === "none" ? [primary].filter(Boolean) : [primary, value].filter(Boolean);
                    setFormData({ ...formData, assigned_staff_ids: newIds });
                  }}
                >
                  <SelectTrigger data-testid="staff-select-2">
                    <SelectValue placeholder="Select second staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Assign Later</SelectItem>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id} disabled={formData.assigned_staff_ids.includes(staff.id)}>
                        {staff.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing & Extras - Always show if user can view financials */}
        {canViewFinancials && (
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Pricing & Extras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Extras/Add-ons */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Add Extras / Add-ons</Label>
                  <p className="text-xs text-zinc-500 mb-3">
                    Add extra services like "Extra Bedroom", "Oven Clean", "Window Cleaning", etc.
                  </p>
                </div>
                
                {/* Add new extra input */}
                <div className="flex gap-2">
                  <Input
                    value={newExtraName}
                    onChange={(e) => setNewExtraName(e.target.value)}
                    placeholder="Extra name (e.g., Extra Bedroom)"
                    className="flex-1"
                    data-testid="extra-name-input"
                  />
                  <div className="relative w-32">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newExtraPrice}
                      onChange={(e) => setNewExtraPrice(e.target.value)}
                      placeholder="Price"
                      className="pl-8"
                      data-testid="extra-price-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addExtra();
                        }
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={addExtra}
                    disabled={!newExtraName.trim() || !newExtraPrice}
                    data-testid="add-extra-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* List of added extras */}
                {formData.extras.length > 0 && (
                  <div className="space-y-2">
                    {formData.extras.map((extra) => (
                      <div 
                        key={extra.id} 
                        className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100"
                      >
                        <div className="flex items-center gap-3">
                          <Plus className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm font-medium text-zinc-700">{extra.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-emerald-600">+{formatPrice(extra.price)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeExtra(extra.id)}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                            data-testid={`remove-extra-${extra.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Package Price Override (for larger houses) */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Package Price Override (ex GST)</Label>
                  <Badge variant="secondary" className="text-xs">Optional</Badge>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  Override the package price for larger houses. Extras will still be added on top.
                </p>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.custom_price_override}
                    onChange={(e) => setFormData({ ...formData, custom_price_override: e.target.value })}
                    placeholder={`Standard: ${formatPrice(basePrice)}`}
                    className="pl-8"
                    data-testid="price-override-input"
                  />
                </div>
              </div>

              {/* Price Notes */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Price Notes / Special Conditions</Label>
                <p className="text-xs text-zinc-500 mb-3">
                  Record why the price was adjusted (e.g., "Large 5 bedroom house", "Repeat customer discount")
                </p>
                <Textarea
                  value={formData.price_notes}
                  onChange={(e) => setFormData({ ...formData, price_notes: e.target.value })}
                  placeholder="e.g., Large house - extra rooms, Repeat customer discount..."
                  rows={2}
                  data-testid="price-notes-input"
                />
              </div>

              {/* Price Summary */}
              <div className="bg-zinc-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">
                    {formData.custom_price_override ? 'Package (Override):' : 'Package:'}
                  </span>
                  <span className={formData.custom_price_override ? 'text-amber-600 font-medium' : 'text-zinc-700'}>
                    {formatPrice(packagePrice)}
                    {formData.custom_price_override && basePrice > 0 && (
                      <span className="text-xs text-zinc-400 ml-1 line-through">{formatPrice(basePrice)}</span>
                    )}
                  </span>
                </div>
                {extrasTotal > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600">Extras ({formData.extras.length}):</span>
                    <span className="text-emerald-600">+{formatPrice(extrasTotal)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
                  <span className="font-medium text-zinc-900">Total (ex GST):</span>
                  <span className="text-lg font-bold text-emerald-600">
                    {formatPrice(finalPrice)}
                  </span>
                </div>
                {formData.custom_price_override && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <span>⚠️</span> Package price overridden (larger than standard)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom Checklist Items - Ad-hoc tasks */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-zinc-500" />
              Custom Checklist Items
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-500">
              Add specific tasks for this job. These will be added to any package checklist items.
            </p>
            
            {/* Add new item input */}
            <div className="flex gap-2">
              <Input
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                placeholder="e.g., Clean inside oven, wash exterior windows..."
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addChecklistItem();
                  }
                }}
                data-testid="custom-checklist-input"
              />
              <Button
                type="button"
                onClick={addChecklistItem}
                disabled={!newChecklistItem.trim()}
                data-testid="add-checklist-item-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            
            {/* List of added items */}
            {formData.custom_checklist_items.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-zinc-600">
                  Added Tasks ({formData.custom_checklist_items.length})
                </Label>
                <div className="space-y-2">
                  {formData.custom_checklist_items.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-100"
                    >
                      <div className="flex items-center gap-2">
                        <CheckSquare className="h-4 w-4 text-zinc-400" />
                        <span className="text-sm text-zinc-700">{item.task}</span>
                        <Badge variant="secondary" className="text-xs">Required</Badge>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeChecklistItem(item.id)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Package checklist preview */}
            {selectedPackage?.checklist_items?.length > 0 && (
              <div className="pt-3 border-t border-zinc-100">
                <Label className="text-sm text-zinc-600">
                  Package Items ({selectedPackage.checklist_items.length} from {selectedPackage.name})
                </Label>
                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                  {selectedPackage.checklist_items.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-zinc-500">
                      <CheckSquare className="h-3 w-3" />
                      <span>{item.task}</span>
                    </div>
                  ))}
                  {selectedPackage.checklist_items.length > 5 && (
                    <p className="text-xs text-zinc-400 italic">
                      +{selectedPackage.checklist_items.length - 5} more items
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal mt-2',
                      !formData.scheduled_date && 'text-muted-foreground'
                    )}
                    data-testid="date-picker-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.scheduled_date ? format(formData.scheduled_date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.scheduled_date}
                    onSelect={(date) => date && setFormData({ ...formData, scheduled_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Select
                  value={formData.time_window_start}
                  onValueChange={(value) => setFormData({ ...formData, time_window_start: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="start-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>End Time</Label>
                <Select
                  value={formData.time_window_end}
                  onValueChange={(value) => setFormData({ ...formData, time_window_end: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="end-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Recurring Booking</Label>
                <p className="text-sm text-zinc-500">Set up a repeating schedule</p>
              </div>
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                data-testid="recurring-switch"
              />
            </div>

            {formData.is_recurring && (
              <div>
                <Label>Recurrence Pattern</Label>
                <Select
                  value={formData.recurrence_pattern}
                  onValueChange={(value) => setFormData({ ...formData, recurrence_pattern: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="recurrence-select">
                    <SelectValue placeholder="Select pattern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Job Details */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Job Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {templates.length > 0 && (
              <div>
                <Label>Service Template (Optional)</Label>
                <Select
                  value={formData.template_id}
                  onValueChange={selectTemplate}
                >
                  <SelectTrigger className="mt-2" data-testid="template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.label} {template.default_price && `- $${template.default_price}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="job_details">Job Description</Label>
              <Textarea
                id="job_details"
                value={formData.job_details}
                onChange={(e) => setFormData({ ...formData, job_details: e.target.value })}
                placeholder="Describe the cleaning requirements..."
                className="mt-2"
                data-testid="job-details-input"
              />
            </div>

            <div>
              <Label htmlFor="access_notes">Access Instructions</Label>
              <Textarea
                id="access_notes"
                value={formData.access_notes}
                onChange={(e) => setFormData({ ...formData, access_notes: e.target.value })}
                placeholder="How to access the property..."
                className="mt-2"
                data-testid="access-notes-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: e.target.value })}
                  className="mt-2"
                  data-testid="duration-input"
                />
              </div>
              <div>
                <Label htmlFor="price">Price (Optional)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="$0.00"
                  className="mt-2"
                  data-testid="price-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Deposit Required</Label>
                <p className="text-sm text-zinc-500">Request a deposit for this job</p>
              </div>
              <Switch
                checked={formData.deposit_required}
                onCheckedChange={(checked) => setFormData({ ...formData, deposit_required: checked })}
                data-testid="deposit-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/bookings')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-zinc-900 hover:bg-zinc-800"
            disabled={loading}
            data-testid="create-booking-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Booking'
            )}
          </Button>
        </div>
      </form>
      
      {/* Floating Taco Assistant */}
      <FloatingTacoWidget 
        context="booking_creation"
        contextData={{
          client: formData.client_id ? {
            name: formData.client_name,
            service_address: formData.service_address
          } : null,
          package: formData.package_name
        }}
      />
    </div>
  );
};

export default NewBookingPage;
